import { $generateNodesFromDOM } from '@lexical/html';
import {
  $createParagraphNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  ElementNode,
  LexicalEditor,
  LexicalNode,
} from 'lexical';

export function clearEditor(editor: LexicalEditor) {
  editor.update(() => {
    $getRoot().clear();
  });
}

/**
 * Updates the editor content given a stringified HTML content.
 */
export const setEditorContent = ({
  editor,
  contents,
}: {
  editor: LexicalEditor;
  contents: string | undefined;
}) => {
  if (!contents) {
    return;
  }

  editor.update(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(contents, 'text/html');

    // Remove any empty table nodes, as that makes lexical throws an error if the table does not contain any rows.
    Array.from(dom.querySelectorAll('table'))
      .filter((tableNode) => !tableNode.rows.length)
      .forEach((tableNode) => tableNode.remove());

    const nodes = $generateNodesFromDOM(editor, dom);

    const root = $getRoot();
    root.clear();

    nodes.forEach((node) => {
      if ($isElementNode(node) || $isDecoratorNode(node)) {
        root.append(node);
      } else if ($isTextNode(node)) {
        const paragraphNode = $createParagraphNode();
        paragraphNode.append(node);
        root.append(paragraphNode);
      }
    });
  });
};

/**
 * Checks if a Lexical node has any text content, recursively.
 */
const hasActualContent = (node: ElementNode): boolean => {
  if (node.getTextContent().trim().length > 0) {
    return true;
  }

  return node
    .getChildren()
    .some(
      (child) =>
        $isDecoratorNode(child) ||
        ($isElementNode(child) && hasActualContent(child)),
    );
};

/**
 * Traverse a Lexical node tree checking for the `shouldRemove` function and, if true, removes the given node, otherwise
 * it does nothing.
 */
const traverseAndRemove = (
  node: ElementNode,
  shouldRemove: (n: LexicalNode | null | undefined) => boolean,
): void => {
  [...node.getChildren()].forEach((child) => {
    if ($isElementNode(child)) {
      traverseAndRemove(child, shouldRemove);
    }

    if (shouldRemove(child)) {
      child.remove();
    }
  });
};

/**
 * Updates the editor content removing empty HTML tags, duplicated line breaks, and more.
 */
export const cleanHTMLContent = () => {
  const root = $getRoot();

  // Step 1: Remove all line breaks
  traverseAndRemove(root, $isLineBreakNode);

  // Step 2: Remove empty text nodes
  traverseAndRemove(
    root,
    (node) => $isTextNode(node) && node.getTextContent().trim() === '',
  );

  // Step 3: Remove empty elements (bottom-up, excluding root children)
  traverseAndRemove(
    root,
    (node) =>
      $isElementNode(node) &&
      !hasActualContent(node) &&
      node.getParent() !== root,
  );

  // Step 4: Remove empty root-level elements if other content exists
  const rootChildren = root.getChildren();
  const hasContent = rootChildren.some(
    (child) => !$isElementNode(child) || hasActualContent(child),
  );

  if (hasContent) {
    [...rootChildren]
      .filter((child) => $isElementNode(child) && !hasActualContent(child))
      .forEach((child) => child.remove());
  }

  // Ensure at least one paragraph exists
  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
};
